import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Save, Power, PowerOff, Clock, UserCheck, MessageSquare, Hash, Timer } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULTS = {
  masterEnabled: true,
  businessHoursOnly: false,
  businessHoursStart: '09:00',
  businessHoursEnd: '18:00',
  businessHoursTimezone: 'Asia/Kolkata',
  humanHandoffMode: false,
  fallbackReply: '',
  defaultCooldownSeconds: 30,
  maxRepliesPerUserPerDay: 20,
};

const Toggle = ({ checked, onChange, testid }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    data-testid={testid}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
      checked ? 'bg-teal-600' : 'bg-gray-300'
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-5' : 'translate-x-1'
      }`}
    />
  </button>
);

const SettingsTab = () => {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/automation/chatbots/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setSettings({ ...DEFAULTS, ...data });
    } catch (e) {
      console.error('settings load failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    try {
      setSaving(true);
      setError('');
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/automation/chatbots/settings`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save settings');
      }
      const data = await res.json();
      setSettings({ ...DEFAULTS, ...data });
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const update = (key, value) => setSettings((s) => ({ ...s, [key]: value }));

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <Loader2 className="animate-spin text-teal-600" size={28} />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="settings-tab">
      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
        <Row
          icon={settings.masterEnabled ? Power : PowerOff}
          iconClass={settings.masterEnabled ? 'text-green-600 bg-green-50' : 'text-gray-500 bg-gray-100'}
          title="Master switch"
          desc="Instantly enable or disable every chatbot rule."
          control={<Toggle checked={settings.masterEnabled} onChange={(v) => update('masterEnabled', v)} testid="settings-master-toggle" />}
        />

        <Row
          icon={UserCheck}
          iconClass="text-purple-600 bg-purple-50"
          title="Human handoff mode"
          desc="When on, bots stop replying so agents can handle conversations manually."
          control={<Toggle checked={settings.humanHandoffMode} onChange={(v) => update('humanHandoffMode', v)} testid="settings-handoff-toggle" />}
        />

        <Row
          icon={Clock}
          iconClass="text-blue-600 bg-blue-50"
          title="Business hours only"
          desc="Chatbots only reply within the configured window."
          control={<Toggle checked={settings.businessHoursOnly} onChange={(v) => update('businessHoursOnly', v)} testid="settings-hours-toggle" />}
        />

        {settings.businessHoursOnly && (
          <div className="pl-14 grid grid-cols-1 md:grid-cols-3 gap-4 -mt-2">
            <LabeledInput
              label="Start"
              type="time"
              value={settings.businessHoursStart}
              onChange={(v) => update('businessHoursStart', v)}
              testid="settings-hours-start"
            />
            <LabeledInput
              label="End"
              type="time"
              value={settings.businessHoursEnd}
              onChange={(v) => update('businessHoursEnd', v)}
              testid="settings-hours-end"
            />
            <LabeledInput
              label="Timezone"
              value={settings.businessHoursTimezone}
              onChange={(v) => update('businessHoursTimezone', v)}
              placeholder="Asia/Kolkata"
              testid="settings-hours-tz"
            />
          </div>
        )}

        <Row
          icon={MessageSquare}
          iconClass="text-amber-600 bg-amber-50"
          title="Default fallback reply"
          desc="Sent when no rule matches an incoming message. Leave blank to disable."
          control={null}
        />
        <textarea
          value={settings.fallbackReply}
          onChange={(e) => update('fallbackReply', e.target.value)}
          placeholder="e.g., Thanks for reaching out! Our team will get back to you shortly."
          rows={3}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 outline-none"
          data-testid="settings-fallback-input"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3">
            <span className="w-10 h-10 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center">
              <Timer size={18} />
            </span>
            <div className="flex-1">
              <p className="font-medium text-gray-800">Default cooldown (seconds)</p>
              <p className="text-xs text-gray-500 mb-2">
                Minimum gap between replies to the same user per rule.
              </p>
              <input
                type="number"
                min={0}
                value={settings.defaultCooldownSeconds}
                onChange={(e) => update('defaultCooldownSeconds', Number(e.target.value))}
                className="w-40 border border-gray-200 rounded-lg px-3 py-2 text-sm"
                data-testid="settings-cooldown-input"
              />
            </div>
          </div>

          <div className="flex items-start gap-3">
            <span className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
              <Hash size={18} />
            </span>
            <div className="flex-1">
              <p className="font-medium text-gray-800">Max replies per user / day</p>
              <p className="text-xs text-gray-500 mb-2">
                Protects against runaway spam to a single user.
              </p>
              <input
                type="number"
                min={0}
                value={settings.maxRepliesPerUserPerDay}
                onChange={(e) => update('maxRepliesPerUserPerDay', Number(e.target.value))}
                className="w-40 border border-gray-200 rounded-lg px-3 py-2 text-sm"
                data-testid="settings-daily-limit-input"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">{error}</div>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <p className="text-xs text-gray-500">
            {savedAt ? `Saved at ${savedAt}` : 'Changes apply immediately after save.'}
          </p>
          <button
            onClick={save}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
            data-testid="settings-save-btn"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save changes
          </button>
        </div>
      </div>
    </div>
  );
};

const Row = ({ icon: Icon, iconClass, title, desc, control }) => (
  <div className="flex items-start gap-3">
    <span className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconClass}`}>
      <Icon size={18} />
    </span>
    <div className="flex-1">
      <p className="font-medium text-gray-800">{title}</p>
      <p className="text-xs text-gray-500">{desc}</p>
    </div>
    {control}
  </div>
);

const LabeledInput = ({ label, value, onChange, type = 'text', placeholder, testid }) => (
  <div>
    <label className="block text-xs text-gray-500 mb-1">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
      data-testid={testid}
    />
  </div>
);

export default SettingsTab;
